import type { DivergenceRow } from "../api/rest";
import { buildXlsx, type XlsxColumn, type XlsxValue } from "./xlsx";

/**
 * The divergences as a workbook someone can act on (ADR 374): only the rows
 * that are wrong, one per (address, item), each with a sentence in plain
 * Portuguese saying WHAT is wrong — the sheet that goes to whoever fixes the
 * WMS or walks back to the bin. Matches are left out on purpose: the report
 * page shows them; this file is the to-do list.
 *
 * Portuguese in the cells, like the customer stock report (ADR 241): the
 * reader is the customer's operations team, not the dashboard user.
 */

const STATUS: Record<Exclude<DivergenceRow["status"], "match">, string> = {
  qty_mismatch: "Divergência de quantidade",
  missing_in_ic3: "Faltando no iC3",
  unexpected_in_ic3: "Inesperado no iC3",
  received_in_window: "Recebido durante a contagem",
  shipped_in_window: "Expedido durante a contagem",
};

/** ADR 384: the closing snapshot explained these — nobody has to act on them, so the
 *  to-do sheet leaves them out with the matches. */
const EXPLAINED_BY_WINDOW = new Set<DivergenceRow["status"]>(["received_in_window", "shipped_in_window"]);

const DISPOSITION: Record<string, string> = {
  accepted: "Aceita",
  recount: "Recontar",
  adjust_wms: "Ajustar WMS",
};

export const DIVERGENCE_COLUMNS: XlsxColumn[] = [
  { header: "Endereço",          width: 14, type: "text" },
  { header: "Item",              width: 20, type: "text" },
  { header: "Situação",          width: 24, type: "text" },
  { header: "O que está errado", width: 78, type: "text" },
  { header: "WMS",               width: 10, type: "number" },
  { header: "Contado",           width: 10, type: "number" },
  { header: "Diferença",         width: 11, type: "number" },
  { header: "FIFO",              width: 12, type: "date" },
  { header: "Idade (dias)",      width: 12, type: "number" },
  { header: "Decisão",           width: 14, type: "text" },
  { header: "Justificativa",     width: 44, type: "text" },
  { header: "Revisado por",      width: 18, type: "text" },
  { header: "Revisado em",       width: 18, type: "date" },
];

const qty = (n: number) => n.toLocaleString("pt-BR");

/** The sentence a person reads to know what to do at that bin. */
export function explain(r: DivergenceRow): string {
  switch (r.status) {
    case "qty_mismatch": {
      const e = r.expectedQty ?? 0;
      const c = r.countedQty ?? 0;
      const d = c - e;
      return `Quantidade diferente: o WMS registra ${qty(e)} un., a contagem encontrou ${qty(c)} un. (${d > 0 ? "+" : ""}${qty(d)}).`;
    }
    case "missing_in_ic3":
      return r.expectedQty == null
        ? "O WMS registra este item neste endereço; a contagem visitou o endereço e não encontrou o item."
        : `O WMS registra ${qty(r.expectedQty)} un. deste item neste endereço; a contagem visitou o endereço e não encontrou o item.`;
    case "unexpected_in_ic3":
      return r.countedQty == null
        ? "A contagem encontrou este item neste endereço, mas o WMS não registra saldo dele aqui."
        : `A contagem encontrou ${qty(r.countedQty)} un. deste item neste endereço, mas o WMS não registra saldo dele aqui.`;
    case "received_in_window":
      return "A contagem encontrou este item aqui e o WMS de fechamento já o registra: entrou durante a contagem.";
    case "shipped_in_window":
      return "O WMS de referência registrava este item aqui e o de fechamento não registra mais: saiu durante a contagem.";
    default:
      return "";
  }
}

/** ISO date (yyyy-mm-dd) or timestamp → Date, else null. A bad string must not
 *  become an "Invalid Date" cell. */
function asDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function divergenceRows(rows: DivergenceRow[]): XlsxValue[][] {
  return rows
    .filter((r): r is DivergenceRow & { status: keyof typeof STATUS } => r.status !== "match" && !EXPLAINED_BY_WINDOW.has(r.status))
    .map((r) => [
      r.address,
      r.product ?? "",
      STATUS[r.status],
      explain(r),
      r.expectedQty,
      r.countedQty,
      r.status === "qty_mismatch" && r.expectedQty != null && r.countedQty != null ? r.countedQty - r.expectedQty : null,
      asDate(r.fifoDate),
      r.ageDays,
      r.review ? DISPOSITION[r.review.disposition] ?? r.review.disposition : "",
      r.review?.justification ?? "",
      r.review?.reviewedBy ?? "",
      asDate(r.review?.reviewedAt),
    ]);
}

export function divergencesToXlsx(rows: DivergenceRow[], runName: string): Uint8Array {
  // Excel forbids : \ / ? * [ ] in a tab name and caps it at 31 characters.
  const name = `Divergências ${runName}`.replace(/[:\\/?*[\]]/g, " ").slice(0, 31).trim() || "Divergências";
  return buildXlsx({ name, columns: DIVERGENCE_COLUMNS, rows: divergenceRows(rows) });
}
